import { useState, useContext } from 'react';
import { ThemeContext } from './ThemeContext.jsx';
import { routeOptionsFor } from './archiveLore.js';
import ThreadRouteSelect from './ThreadRouteSelect.jsx';

// ─── Constants ────────────────────────────────────────────────────────────────

export const STORY_MODES = [
  {
    value: 'alternate_route',
    label: 'Alternate Route',
    blurb: 'Fresh start. No assumed history. The character knows only what this story establishes. (Default)',
  },
  {
    value: 'canon_continuation',
    label: 'Canon Continuation',
    blurb: 'Picks up from established canon. Relationship stage and history from the source apply.',
  },
  {
    value: 'canon_adjacent',
    label: 'Canon-Adjacent',
    blurb: 'Same world, diverging from a branch point you describe in the context note below.',
  },
];

export const RELATIONSHIP_BASELINES = [
  { value: 'strangers',              label: 'Strangers',              hint: 'No history, no feeling. Pure first impression.' },
  { value: 'acquaintances',          label: 'Acquaintances',          hint: 'Recognition but no real knowledge of each other.' },
  { value: 'one_sided_awareness',    label: 'One-Sided Awareness',    hint: 'Asymmetric knowing — specify direction in the context note.' },
  { value: 'former_rivals',          label: 'Former Rivals',          hint: 'History of conflict, no trust. Respect may exist.' },
  { value: 'former_allies',          label: 'Former Allies',          hint: 'Worked together once, it ended — specify how below.' },
  { value: 'old_friends_estranged',  label: 'Old Friends, Estranged', hint: 'Was close, something broke it — specify what below.' },
  { value: 'past_lovers_separated',  label: 'Past Lovers, Separated', hint: 'Romantic history ended before this story — specify the terms below.' },
  { value: 'custom',                 label: 'Custom',                 hint: 'Free text — describe the full baseline in the context note.' },
];

const BASELINE_DESCRIPTIONS = {
  strangers:
    'You and {player} have never met. Your behavior in this opening is whatever your default is toward an unfamiliar person.',
  acquaintances:
    'You recognize {player} but have no real relationship. You know surface details — name, possibly role — nothing emotionally weighted.',
  one_sided_awareness:
    'The knowing here is asymmetric. Read the context note for which direction it runs, and behave accordingly.',
  former_rivals:
    'You and {player} have a history of conflict. There is no trust here. Whatever civility exists is a choice you are actively making.',
  former_allies:
    'You and {player} once worked together. That ended — read the context note. The residue of that collaboration colors every word.',
  old_friends_estranged:
    'You and {player} were close once. Something broke it — read the context note. The wound is still there, even if scarred over.',
  past_lovers_separated:
    'You and {player} were romantically involved before this story begins. It ended — read the context note for terms. Your body remembers things your mind would rather not.',
  custom:
    'Read the context note for the specific relational baseline.',
};

// ─── Prompt Builder (exported — used by characterIdentity.js) ─────────────────

export function buildStorySetupPrompt(storySetup, charName, playerLabel) {
  if (!storySetup) return '';
  const { storyMode, relationshipBaseline, cowriterContextNote } = storySetup;
  if (!storyMode && !relationshipBaseline && !cowriterContextNote) return '';

  const player = playerLabel || 'your conversation partner';
  const lines = [];

  // STORY MODE
  const modeLabel = STORY_MODES.find(m => m.value === storyMode)?.label || 'Alternate Route';
  lines.push(`STORY MODE: ${modeLabel}`);
  lines.push('');

  if (storyMode === 'canon_continuation') {
    lines.push(
      `This story continues from established canon. Your history with ${player} is real and present. ` +
      `The relationship stage and emotional residue from your shared past inform every moment.`
    );
  } else if (storyMode === 'canon_adjacent') {
    lines.push(
      `This story branches from canon at a point described in the setup note below. Treat events ` +
      `prior to that branch as having happened; treat anything after it as not-yet-written. Your ` +
      `knowledge and feelings reflect the divergence point, not the full canon arc.`
    );
  } else {
    // alternate_route (default)
    lines.push(
      `This is a self-contained story. You begin with no established history with ${player} unless ` +
      `it has been set up in the context note below. Do not import feelings, familiarity, or ` +
      `relationship assumptions from any other timeline. This story's emotional clock starts at zero.`
    );
    lines.push('');
    lines.push(
      `CRITICAL: Do not accelerate toward intimacy because the co-writer's character does something ` +
      `kind or interesting. Attraction registers internally — it does not immediately shift your ` +
      `behavior. Trust is built through action over time. One scene does not establish trust. One ` +
      `conversation does not establish closeness. Your defenses lower at the rate your psychology ` +
      `allows — not at the rate the story requires.`
    );
  }

  // RELATIONSHIP BASELINE
  if (relationshipBaseline) {
    const baseline = RELATIONSHIP_BASELINES.find(b => b.value === relationshipBaseline);
    if (baseline) {
      lines.push('');
      lines.push(`RELATIONSHIP BASELINE: ${baseline.label}`);
      const desc = (BASELINE_DESCRIPTIONS[relationshipBaseline] || '').replace(/\{player\}/g, player);
      if (desc) lines.push(desc);
    }
  }

  // COWRITER CONTEXT NOTE
  if (cowriterContextNote && cowriterContextNote.trim()) {
    lines.push('');
    lines.push('STORY SETUP (from co-writer):');
    lines.push(cowriterContextNote.trim());
  }

  // INFER instruction — literal pass-through to the model
  lines.push('');
  lines.push(
    `⟪INFER⟫ Given this story mode, relationship baseline, and setup context: what does ${charName} ` +
    `want in this opening moment? What are they protecting? What would make them more guarded or ` +
    `more open than their default? Write from that interior state.`
  );

  return lines.join('\n');
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * StorySetupPanel
 *
 * mode="setup"  — full-screen modal shown before first message. Primary CTA is "Begin Story".
 * mode="edit"   — same fields surfaced inside StoryPreferencesPanel mid-conversation.
 *
 * Props:
 *   prefs              — current story_preferences object (from dm_conversations)
 *   onChange(nextPrefs) — called with merged prefs on save
 *   onClose            — called to dismiss the panel
 *   characterName      — display name of the AI character
 *   mode               — 'setup' | 'edit'  (default: 'setup')
 */
export default function StorySetupPanel({ prefs, onChange, onClose, characterName, characterId = null, mode = 'setup' }) {
  const { theme: t } = useContext(ThemeContext);

  const [storyMode, setStoryMode]   = useState(prefs?.story_mode || 'alternate_route');
  const [baseline, setBaseline]     = useState(prefs?.relationship_baseline || 'strangers');
  const [note, setNote]             = useState(prefs?.cowriter_context_note || '');
  const [route, setRoute]           = useState(prefs?.canon_route || '');

  const commit = () => {
    onChange({
      ...prefs,
      story_mode: storyMode,
      relationship_baseline: baseline,
      cowriter_context_note: note,
      canon_route: route || '',
    });
    if (onClose) onClose();
  };

  const baselineHint = RELATIONSHIP_BASELINES.find(b => b.value === baseline)?.hint;

  // ── Styles ──────────────────────────────────────────────────────────────────
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1200, padding: '20px',
  };

  const panel = {
    background: t.bgBase || '#1a1612',
    border: `1px solid ${t.accentGold || 'var(--ds-gold, #c4a882)'}`,
    borderRadius: '10px', maxWidth: '600px', width: '100%',
    maxHeight: '90vh', overflowY: 'auto', padding: '28px 32px',
    color: t.textPrimary,
    boxShadow: '0 8px 40px rgba(0,0,0,0.7)',
  };

  const sectionLabel = {
    fontFamily: "'Cinzel', serif", fontSize: '13px',
    color: t.accentGold || 'var(--ds-gold, #c4a882)', textTransform: 'uppercase',
    letterSpacing: '1.5px', marginTop: '22px', marginBottom: '10px', display: 'block',
  };

  const modeCard = (selected) => ({
    border: selected ? `2px solid ${t.accentGold || 'var(--ds-gold, #c4a882)'}` : `1px solid ${t.borderMedium || '#3a3228'}`,
    background: selected ? 'var(--ds-gold-a1a, rgba(196,168,130,0.10))' : (t.bgInput || '#221c16'),
    borderRadius: '8px', padding: '12px 16px', marginBottom: '8px',
    cursor: 'pointer', transition: 'all 0.15s ease',
  });

  const selectStyle = {
    width: '100%', background: t.bgInput || '#221c16', color: t.textPrimary,
    border: `1px solid ${t.borderMedium || '#3a3228'}`, borderRadius: '6px',
    padding: '10px 12px', fontSize: '13px', fontFamily: "'Inter', system-ui, sans-serif",
    outline: 'none', cursor: 'pointer',
  };

  const textareaStyle = {
    width: '100%', minHeight: '110px', background: t.bgInput || '#221c16',
    color: t.textPrimary, border: `1px solid ${t.borderMedium || '#3a3228'}`,
    borderRadius: '6px', padding: '10px 12px', fontSize: '13px',
    fontFamily: "'Inter', system-ui, sans-serif", resize: 'vertical',
    boxSizing: 'border-box', outline: 'none',
  };

  const primaryBtn = {
    background: t.accentGold || 'var(--ds-gold, #c4a882)', color: '#1a1612', border: 'none',
    padding: '11px 26px', borderRadius: '6px', cursor: 'pointer',
    fontFamily: "'Cinzel', serif", fontSize: '13px',
    letterSpacing: '1px', fontWeight: 700,
  };

  const secondaryBtn = {
    background: 'transparent', color: t.textMuted,
    border: `1px solid ${t.borderMedium || '#3a3228'}`,
    padding: '11px 20px', borderRadius: '6px', cursor: 'pointer',
    fontSize: '13px',
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={overlay} onClick={mode === 'setup' ? undefined : onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
          <div>
            <div style={{ fontFamily: "'Cinzel', serif", fontSize: '20px', color: t.accentGold || 'var(--ds-gold, #c4a882)', letterSpacing: '0.05em' }}>
              {mode === 'setup' ? 'Set the Scene' : 'Story Frame'}
            </div>
            <div style={{ fontSize: '13px', color: t.textMuted, marginTop: '4px', fontStyle: 'italic' }}>
              {mode === 'setup'
                ? `Tell ${characterName || 'the character'} what kind of story this is before you begin.`
                : `Adjust the story framing for this conversation with ${characterName || 'this character'}.`}
            </div>
          </div>
          {mode === 'edit' && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.textMuted, fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>✕</button>
          )}
        </div>

        {/* Story Mode */}
        <span style={sectionLabel}>Story Mode</span>
        {STORY_MODES.map(m => (
          <div key={m.value} style={modeCard(storyMode === m.value)} onClick={() => setStoryMode(m.value)}>
            <div style={{ fontWeight: 700, color: t.textPrimary, marginBottom: '3px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {storyMode === m.value && <span style={{ color: t.accentGold || 'var(--ds-gold, #c4a882)', fontSize: '12px' }}>●</span>}
              {m.label}
            </div>
            <div style={{ fontSize: '12px', color: t.textSecondary, lineHeight: 1.4 }}>{m.blurb}</div>
          </div>
        ))}

        {/* Relationship Baseline */}
        <span style={sectionLabel}>Relationship Baseline</span>
        <select style={selectStyle} value={baseline} onChange={e => setBaseline(e.target.value)}>
          {RELATIONSHIP_BASELINES.map(b => (
            <option key={b.value} value={b.value}>{b.label}</option>
          ))}
        </select>
        {baselineHint && (
          <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '6px', fontStyle: 'italic' }}>{baselineHint}</div>
        )}

        {/* This thread's route (2026-10-12) */}
        {characterId && routeOptionsFor(characterId).length > 0 && (
          <>
            <span style={sectionLabel}>This Thread's Route</span>
            <ThreadRouteSelect
              characterId={characterId}
              value={route}
              onChange={setRoute}
              selectStyle={selectStyle}
              hintStyle={{ fontSize: '11px', color: t.textFaint || t.textMuted, marginTop: '5px' }}
            />
          </>
        )}

        {/* Context Note */}
        <span style={sectionLabel}>Context Note</span>
        <textarea
          style={textareaStyle}
          placeholder="Set the scene — who are you, what's the situation, what tone are you going for? Any details the character should know before your first line."
          value={note}
          onChange={e => setNote(e.target.value)}
        />
        <div style={{ fontSize: '11px', color: t.textFaint || t.textMuted, marginTop: '5px' }}>
          This is directorial intent — it shapes how the character enters the scene, not what they know as fact.
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
          {mode === 'edit' && (
            <button style={secondaryBtn} onClick={onClose}>Cancel</button>
          )}
          <button style={primaryBtn} onClick={commit}>
            {mode === 'setup' ? 'Begin Story' : 'Save Changes'}
          </button>
        </div>

      </div>
    </div>
  );
}
